import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, History, ChevronRight, Sparkles, Mic, MicOff, WifiOff, CloudUpload, FlaskConical, Satellite } from "lucide-react";
import { TopBar, BottomNav } from "../components/Nav";
import { UploadZone } from "../components/UploadZone";
import { WeatherWidget } from "../components/WeatherWidget";
import { API, useAuth } from "../context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { useVoiceInput } from "../hooks/useVoiceInput";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import { enqueueScan, getQueuedScans, removeQueuedScan, recordFailedAttempt } from "../lib/scanQueue";
import { useLang } from "../i18n/LanguageContext";

const MODELS = [
  { id: "auto|auto", label: "Auto (Smart)", provider: "auto", name: "auto" },
  { id: "gemini|gemini-3-flash-preview", label: "Gemini 3 Flash", provider: "gemini", name: "gemini-3-flash-preview" },
  { id: "openai|gpt-5.2", label: "GPT-5.2 (OpenAI)", provider: "openai", name: "gpt-5.2" },
  { id: "anthropic|claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5", provider: "anthropic", name: "claude-sonnet-4-5-20250929" },
];

export default function DashboardPage() {
  const { user } = useAuth();
  const { lang, setLang, t } = useLang();
  const nav = useNavigate();
  const [image, setImage] = useState(null);
  const [cropHint, setCropHint] = useState("");
  const [modelId, setModelId] = useState(MODELS[0].id);
  const [diagnosing, setDiagnosing] = useState(false);
  const [recent, setRecent] = useState([]);
  const [fields, setFields] = useState([]);
  const [fieldId, setFieldId] = useState("none");
  const [queuedCount, setQueuedCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const online = useOnlineStatus();

  const refreshQueue = useCallback(async () => {
    const q = await getQueuedScans();
    setQueuedCount(q.length);
  }, []);

  const voice = useVoiceInput(lang, {
    onResult: (text) => {
      setCropHint((prev) => (prev ? `${prev} ${text}` : text).trim());
      toast.success(t("dash.voiceCaptured"));
    },
  });

  useEffect(() => {
    if (voice.error) toast.error(`Voice: ${voice.error}`);
  }, [voice.error]);

  useEffect(() => {
    refreshQueue();
    axios.get(`${API}/scans?limit=5`).then((r) => setRecent(r.data)).catch(() => {});
    axios.get(`${API}/fields`).then((r) => setFields(r.data)).catch(() => {});
  }, [refreshQueue]);

  const syncQueue = useCallback(async () => {
    const q = await getQueuedScans();
    if (q.length === 0) return;
    setSyncing(true);
    let ok = 0, fail = 0, dropped = 0;
    for (const item of q) {
      try {
        await axios.post(`${API}/diagnose`, {
          image_base64: item.image_base64,
          model_provider: item.model_provider,
          model_name: item.model_name,
          language: item.language,
          crop_hint: item.crop_hint,
          field_id: item.field_id || null,
        });
        await removeQueuedScan(item.queued_id);
        ok += 1;
      } catch {
        const res = await recordFailedAttempt(item.queued_id);
        if (res.dropped) dropped += 1; else fail += 1;
      }
    }
    await refreshQueue();
    setSyncing(false);
    if (ok > 0) {
      toast.success(`Uploaded ${ok}`);
      axios.get(`${API}/scans?limit=5`).then((r) => setRecent(r.data)).catch(() => {});
    }
    if (dropped > 0) toast.error(`Dropped ${dropped} after retries`);
    if (fail > 0) toast.message(`${fail} will retry`);
  }, [refreshQueue]);

  useEffect(() => {
    if (online && queuedCount > 0) syncQueue();
  }, [online, queuedCount, syncQueue]);

  const diagnose = async () => {
    if (!image) {
      toast.error(t("dash.uploadFirst"));
      return;
    }
    const model = MODELS.find((m) => m.id === modelId);
    const fieldArg = fieldId !== "none" ? fieldId : null;

    if (!online) {
      await enqueueScan({
        image_base64: image,
        model_provider: model.provider,
        model_name: model.name,
        language: lang,
        crop_hint: cropHint || null,
        field_id: fieldArg,
      });
      await refreshQueue();
      toast.success(t("dash.savedOffline"));
      setImage(null);
      setCropHint("");
      return;
    }

    setDiagnosing(true);
    try {
      const r = await axios.post(`${API}/diagnose`, {
        image_base64: image,
        model_provider: model.provider,
        model_name: model.name,
        language: lang,
        crop_hint: cropHint || null,
        field_id: fieldArg,
      });
      toast.success(t("dash.diagnosisReady"));
      nav(`/scan/${r.data.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (!detail) {
        await enqueueScan({
          image_base64: image,
          model_provider: model.provider,
          model_name: model.name,
          language: lang,
          crop_hint: cropHint || null,
          field_id: fieldArg,
        });
        await refreshQueue();
        toast.error(t("dash.networkIssue"));
      } else {
        toast.error(detail);
      }
    } finally {
      setDiagnosing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-24 md:pb-10">
      <TopBar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
        <div className="cd-fade-up">
          <h1 className="cd-heading text-3xl sm:text-4xl font-extrabold">
            {t("dash.greet", { name: user?.name?.split(" ")[0] || "" })}
          </h1>
          <p className="text-[#52796F] mt-1">{t("dash.subtitle")}</p>
        </div>

        {(!online || queuedCount > 0) && (
          <div
            data-testid="offline-banner"
            className={`flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-sm ${
              !online
                ? "bg-[#E9C46A]/30 text-[#1C1C1C] border border-[#E9C46A]"
                : "bg-[#52796F]/10 text-[#1B4332] border border-[#52796F]/30"
            }`}
          >
            <div className="flex items-center gap-2 font-semibold">
              {!online ? (
                <><WifiOff className="w-4 h-4" /> {t("dash.offlineBanner")}</>
              ) : (
                <>
                  <CloudUpload className="w-4 h-4" />
                  {syncing
                    ? (queuedCount > 1 ? t("dash.syncingPlural", { n: queuedCount }) : t("dash.syncing", { n: queuedCount }))
                    : (queuedCount > 1 ? t("dash.queuedPendingPlural", { n: queuedCount }) : t("dash.queuedPending", { n: queuedCount }))
                  }
                </>
              )}
            </div>
            {online && queuedCount > 0 && !syncing && (
              <button onClick={syncQueue} data-testid="sync-now-btn" className="text-xs font-bold underline hover:no-underline">
                {t("dash.syncNow")}
              </button>
            )}
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <UploadZone preview={image} onImage={setImage} onClear={() => setImage(null)} />

            <div className="bg-white rounded-2xl p-5 border border-[#E5E0D8] shadow-sm space-y-4">
              <div>
                <label className="text-sm font-semibold text-[#1C1C1C]">
                  {t("dash.cropHint")} {t("common.optional")}
                </label>
                <div className="mt-1 flex gap-2">
                  <input
                    data-testid="crop-hint-input"
                    value={cropHint}
                    onChange={(e) => setCropHint(e.target.value)}
                    placeholder={t("dash.cropHint.placeholder")}
                    className="flex-1 bg-white border border-[#E5E0D8] rounded-xl px-4 py-3 focus:outline-none focus:border-[#1B4332] focus:ring-1 focus:ring-[#1B4332]"
                  />
                  {voice.supported && (
                    <button
                      type="button"
                      data-testid="voice-input-btn"
                      onClick={() => (voice.listening ? voice.stop() : voice.start())}
                      className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                        voice.listening ? "bg-[#D90429] text-white animate-pulse" : "bg-[#F4F1EA] text-[#1B4332] hover:bg-[#E5E0D8]"
                      }`}
                    >
                      {voice.listening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                    </button>
                  )}
                </div>
                {voice.listening && (
                  <p data-testid="voice-listening-indicator" className="text-xs text-[#D90429] mt-1.5 font-medium">
                    {t("dash.listening")}
                  </p>
                )}
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-semibold text-[#1C1C1C]">{t("dash.aiModel")}</label>
                  <Select value={modelId} onValueChange={setModelId}>
                    <SelectTrigger data-testid="model-selector" className="mt-1 rounded-xl border-[#E5E0D8]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MODELS.map((m) => (
                        <SelectItem key={m.id} value={m.id} data-testid={`model-option-${m.provider}`}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#1C1C1C]">{t("dash.language")}</label>
                  <Select value={lang} onValueChange={setLang}>
                    <SelectTrigger data-testid="language-selector" className="mt-1 rounded-xl border-[#E5E0D8]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en" data-testid="lang-option-en">English</SelectItem>
                      <SelectItem value="hi" data-testid="lang-option-hi">हिन्दी</SelectItem>
                      <SelectItem value="mr" data-testid="lang-option-mr">मराठी</SelectItem>
                      <SelectItem value="ta" data-testid="lang-option-ta">தமிழ்</SelectItem>
                      <SelectItem value="te" data-testid="lang-option-te">తెలుగు</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#1C1C1C]">{t("dash.field")}</label>
                  <Select value={fieldId} onValueChange={setFieldId}>
                    <SelectTrigger data-testid="field-selector" className="mt-1 rounded-xl border-[#E5E0D8]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" data-testid="field-option-none">{t("dash.field.none")}</SelectItem>
                      {fields.map((f) => (
                        <SelectItem key={f.id} value={f.id} data-testid={`field-option-${f.id}`}>
                          {f.name}{f.crop_name ? ` · ${f.crop_name}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <button
                data-testid="diagnose-btn"
                onClick={diagnose}
                disabled={!image || diagnosing}
                className="w-full bg-[#1B4332] text-white hover:bg-[#2D6A4F] py-4 rounded-full font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {diagnosing ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> {t("dash.diagnosing")}</>
                ) : !online ? (
                  <><WifiOff className="w-5 h-5" /> {t("dash.saveOffline")}</>
                ) : (
                  <><Sparkles className="w-5 h-5" /> {t("dash.diagnose")}</>
                )}
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <WeatherWidget language={lang} />

            <button
              data-testid="dash-verify-medicine-btn"
              onClick={() => nav("/chemical-check")}
              className="w-full bg-white rounded-2xl p-5 border border-[#E5E0D8] shadow-sm text-left hover:border-[#1B4332] hover:shadow-md transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#1B4332] flex items-center justify-center shrink-0 group-hover:bg-[#2D6A4F]">
                  <FlaskConical className="w-5 h-5 text-[#D4A373]" />
                </div>
                <div className="min-w-0">
                  <div className="cd-heading font-bold text-base text-[#1C1C1C]">{t("dash.verifyMedicine")}</div>
                  <div className="text-xs text-[#52796F] mt-1 leading-snug">{t("dash.verifyMedicineSub")}</div>
                </div>
              </div>
            </button>

            <button
              data-testid="dash-soil-report-btn"
              onClick={() => nav("/soil-report")}
              className="w-full bg-white rounded-2xl p-5 border border-[#E5E0D8] shadow-sm text-left hover:border-[#1B4332] hover:shadow-md transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#52796F] flex items-center justify-center shrink-0 group-hover:bg-[#1B4332]">
                  <Satellite className="w-5 h-5 text-[#D4A373]" />
                </div>
                <div className="min-w-0">
                  <div className="cd-heading font-bold text-base text-[#1C1C1C] flex items-center gap-1.5">
                    {t("dash.soilReport")}
                    <span className="text-[9px] uppercase font-bold bg-[#D4A373] text-[#1C1C1C] px-1.5 py-0.5 rounded-full">NEW</span>
                  </div>
                  <div className="text-xs text-[#52796F] mt-1 leading-snug">{t("dash.soilReportSub")}</div>
                </div>
              </div>
            </button>

            <div className="bg-white rounded-2xl p-5 border border-[#E5E0D8] shadow-sm" data-testid="recent-scans-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="cd-heading font-bold text-lg flex items-center gap-2">
                  <History className="w-4 h-4 text-[#52796F]" /> {t("dash.recentScans")}
                </h3>
                <button data-testid="view-all-scans" onClick={() => nav("/history")} className="text-xs text-[#1B4332] font-semibold hover:underline">
                  {t("dash.viewAll")}
                </button>
              </div>
              {recent.length === 0 && (
                <p className="text-sm text-[#52796F]">{t("dash.noScans")}</p>
              )}
              <ul className="space-y-3">
                {recent.map((s) => (
                  <li
                    key={s.id}
                    data-testid={`recent-scan-${s.id}`}
                    onClick={() => nav(`/scan/${s.id}`)}
                    className="flex items-center gap-3 cursor-pointer cd-lift rounded-xl p-2 -mx-2"
                  >
                    <img src={s.thumbnail || s.image_base64} alt="thumb" className="w-12 h-12 object-cover rounded-lg" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate">
                        {s.result?.healthy ? t("diag.healthy") : s.result?.disease_name || t("diag.unknown")}
                      </div>
                      <div className="text-xs text-[#52796F] truncate">
                        {s.result?.crop_name || "Plant"} · {s.result?.confidence || 0}%
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#52796F]" />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </main>
      <BottomNav />
    </div>
  );
}
